import { cn } from 'noddi-ui-common';
import { commonTexts, treadDepthTone, type TreadDepthThresholds, type TreadDepthTone } from 'noddi-util';
import type { ReactNode } from 'react';

// A new tire is about 8 mm deep, so the scale stops there; a deeper reading fills the bar.
const SCALE_MM = 8;

const TONE_TEXT_CLASS: Record<TreadDepthTone, string> = {
  good: '',
  soon: 'text-warningDark',
  worn: 'text-dangerDark'
};

// Traffic light zones (Claude Design "Tire Offer"): illegal up to the legal minimum, amber up to the recommended
// one, green above. The legend swatch for each minimum takes the color of the zone it ends.
const LEGAL_ZONE_CLASS = 'bg-danger';
const RECOMMENDED_ZONE_CLASS = 'bg-warning/70';
const GOOD_ZONE_CLASS = 'bg-success50';

type TreadDepthMeterLegendTranslations = {
  legalMinimum: string;
  recommendedMinimum: string;
};

type Props = {
  valueMm: number;
  /** Null when the car's country has no tread depth regulation: the bar then has no ticks and no tone. */
  thresholds: TreadDepthThresholds | null;
  /** Names the reading, e.g. the wheel position; also the meter's accessible name. */
  label: string;
  icon?: ReactNode;
  /** Formats a depth without the unit; defaults to one decimal with a point. */
  formatNumber?: (valueMm: number) => string;
};

/**
 * Measured tread as a marker on a 0–8 mm bar, colored red, amber and green at the season's legal and recommended
 * minimum, with 0, both minimums and 8 mm under it. Render one {@link TreadDepthMeterLegend} below the meters to name
 * the zones.
 */
export const TreadDepthMeter = ({ valueMm, thresholds, label, icon, formatNumber = formatDecimal }: Props) => {
  const formatMm = (mm: number) => `${formatNumber(mm)} mm`;
  const tone = thresholds ? treadDepthTone(valueMm, thresholds) : null;

  return (
    <div className='flex flex-col gap-2.5'>
      <div className='flex items-center justify-between gap-2 text-xs'>
        <span className='flex items-center gap-2 font-medium'>
          {icon}
          {label}
        </span>
        <span className={cn('font-semibold', tone && TONE_TEXT_CLASS[tone])}>{formatMm(valueMm)}</span>
      </div>
      <div
        role='meter'
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={SCALE_MM}
        aria-valuenow={valueMm}
        aria-valuetext={formatMm(valueMm)}
        className='relative h-2'
      >
        <div className='flex h-full overflow-hidden rounded-full bg-muted'>
          {thresholds && (
            <>
              <div className={LEGAL_ZONE_CLASS} style={{ width: `${percentOfScale(thresholds.limitMm)}%` }} />
              <div
                className={RECOMMENDED_ZONE_CLASS}
                style={{ width: `${percentOfScale(thresholds.recommendedMm) - percentOfScale(thresholds.limitMm)}%` }}
              />
              <div className={cn('flex-1', GOOD_ZONE_CLASS)} />
            </>
          )}
        </div>
        <div
          aria-hidden
          className='absolute top-1/2 h-5 w-1 -translate-1/2 rounded-full bg-primary ring-2 ring-card'
          style={{ left: `${percentOfScale(valueMm)}%` }}
        />
      </div>
      <div aria-hidden className='relative h-4 text-xs text-muted-foreground'>
        <span className='absolute left-0'>0</span>
        {thresholds &&
          thresholdTicks(thresholds).map((tick) => (
            <span
              key={tick.labelKey}
              className='absolute -translate-x-1/2'
              style={{ left: `${percentOfScale(tick.valueMm)}%` }}
            >
              {formatNumber(tick.valueMm)}
            </span>
          ))}
        <span className='absolute right-0'>{SCALE_MM} mm</span>
      </div>
    </div>
  );
};

type LegendProps = {
  thresholds: TreadDepthThresholds;
  translations?: TreadDepthMeterLegendTranslations;
  formatNumber?: (valueMm: number) => string;
};

/** Names the zone borders on {@link TreadDepthMeter} with their values in mm. */
export const TreadDepthMeterLegend = ({ thresholds, translations, formatNumber = formatDecimal }: LegendProps) => {
  const texts = translations ?? commonTexts.wheelMeasurements;

  return (
    <ul className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground'>
      {thresholdTicks(thresholds).map((tick) => (
        <li key={tick.labelKey} className='flex items-center gap-1.5'>
          <span aria-hidden className={cn('size-2 rounded-full', tick.className)} />
          {texts[tick.labelKey]}
          <span className='font-semibold text-foreground'>{formatNumber(tick.valueMm)} mm</span>
        </li>
      ))}
    </ul>
  );
};

const percentOfScale = (valueMm: number) => Math.min(100, Math.max(0, (valueMm / SCALE_MM) * 100));

const formatDecimal = (valueMm: number) => valueMm.toFixed(1);

type ThresholdTick = {
  valueMm: number;
  className: string;
  labelKey: keyof TreadDepthMeterLegendTranslations;
};

const thresholdTicks = ({ limitMm, recommendedMm }: TreadDepthThresholds): ThresholdTick[] => [
  { valueMm: limitMm, className: LEGAL_ZONE_CLASS, labelKey: 'legalMinimum' },
  { valueMm: recommendedMm, className: RECOMMENDED_ZONE_CLASS, labelKey: 'recommendedMinimum' }
];
